import React, { useEffect, useState } from "react";
import { Accordion, Button, Form, Row, Col } from "react-bootstrap";
import { AiOutlineExclamationCircle, AiOutlineFileText } from "react-icons/ai";
import {
  approveOnboardingDocuments,
  getEmployeeOnboardingDocuments,
  rejectOnboardingDocuments,
} from "../../../services/onBoardingServices";
import FilePreviewer from "../../../components/FilePreviewer";
import { FcApproval } from "react-icons/fc";
import Swal from "sweetalert2";
import Loader from "../../../components/Loader";
import Breadcrumbs from "../../../components/Breadcrumbs";
import { useTranslation } from "react-i18next";
import { useLocation, useParams } from "react-router-dom";
import { decrypt } from "../../../utils/crypto";

const OnboardingDocumentReview = () => {
  let { employee_id } = useParams();
  employee_id = decrypt(employee_id);
  const location = useLocation();
  const stepData = location.state?.step;
  const { t } = useTranslation();
  const loginEmployee = localStorage.getItem("employeeID");
  const [issueTexts, setIssueTexts] = useState({});
  const [activeAccordion, setActiveAccordion] = useState(null);
  const [employeeDocuments, setEmployeeDocuments] = useState([]);
  const [loading, setLoading] = useState({
    show: false,
    message: "",
  });

  const fetchEmployeeDocuments = async () => {
    try {
      const result = await getEmployeeOnboardingDocuments(employee_id);
      setEmployeeDocuments(result?.employeeDocuments);
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    if (employee_id) {
      fetchEmployeeDocuments();
    }
  }, []);

  const approveDocument = async (documentId) => {
    setLoading({
      show: true,
      message: `Verifying...`,
    });
    try {
      const data = { verified_by: loginEmployee, step_id: stepData.stepId };
      const result = await approveOnboardingDocuments(
        employee_id,
        documentId,
        data
      );
      if (result?.success == true) {
        await fetchEmployeeDocuments();
        Swal.fire({
          title: "Documents",
          titleText: "Document verified successfully.",
          icon: "success",
          timer: 1500,
        });
      }
    } catch (error) {
      Swal.fire({
        title: "Documents",
        titleText: "Something went wrong!",
        icon: "warning",
        timer: 1500,
      });
    } finally {
      setLoading({
        show: false,
        message: "",
      });
    }
  };

  const raiseIssueOnDocument = async (documentId) => {
    setLoading({
      show: true,
      message: `Raising...`,
    });
    try {
      const data = {
        verified_by: loginEmployee,
        reason: issueTexts[documentId] || "",
      };
      const result = await rejectOnboardingDocuments(
        employee_id,
        documentId,
        data
      );
      if (result?.success == true) {
        setIssueTexts((prev) => ({ ...prev, [documentId]: "" }));
        await fetchEmployeeDocuments();
        Swal.fire({
          title: "Documents",
          titleText: "Issue Raised successfully.",
          icon: "success",
          timer: 1500,
        });
      }
    } catch (error) {
      Swal.fire({
        title: "Documents",
        titleText: "Something went wrong!",
        icon: "warning",
        timer: 1500,
      });
    } finally {
      setLoading({
        show: false,
        message: "",
      });
    }
  };

  return (
    <div className="page-wrapper">
      {/* Page Content */}
      <div className="content container-fluid">
        <Breadcrumbs
          maintitle={t("Onboarding Documents")}
          title={t("Onboarding Details")}
          subtitle={t("Documents")}
        />
        <Accordion defaultActiveKey="0">
          {Object.entries(employeeDocuments).length !== 0 ? (
            Object.entries(employeeDocuments)?.map(([key, doc], index) => (
              <Accordion.Item eventKey={index.toString()} key={index}>
                <Accordion.Header
                  onClick={() => {
                    setActiveAccordion(doc.document_id);
                  }}
                >
                  <div className="d-flex justify-content-between w-100 me-2 gap-3 align-items-center">
                    <div className="d-flex align-items-center gap-2">
                      <AiOutlineFileText size={18} />
                      <div>
                        <div className="fw-semibold text-capitalize">
                          {doc.document_name}
                        </div>
                        <div className="text-muted small">
                          {doc.document_note}
                        </div>
                      </div>
                    </div>
                    <span
                      className={`badge badge-${
                        doc.verification.status == "pending"
                          ? "warning"
                          : doc.verification.status == "approved"
                          ? "success"
                          : "danger"
                      }`}
                    >
                      {doc.verification.status}
                    </span>
                  </div>
                </Accordion.Header>
                <Accordion.Body>
                  <Row>
                    <Col md={doc.verification.status !== "approved" ? 6 : 12}>
                      <div
                        className="d-flex justify-content-center align-items-center"
                        style={{
                          height: "300px",
                          width: "100%",
                          borderRadius: "12px",
                          overflow: "hidden",
                          marginBottom: "1.5rem",
                        }}
                      >
                        {" "}
                        <FilePreviewer fileSource={doc.file} />
                      </div>
                    </Col>
                    {doc.verification.status !== "approved" && (
                      <Col md={6}>
                        <div className="mb-3">
                          {doc.document_number && (
                            <>
                              {" "}
                              <div className="fw-semibold mb-1">
                                Document Number
                              </div>
                              <div className="d-flex align-items-center gap-2 text-dark ">
                                {doc.document_number ?? "-"}
                              </div>{" "}
                            </>
                          )}
                          <div className="fw-semibold mb-1">Status</div>
                          <div className="d-flex align-items-center gap-2 text-secondary">
                            {doc.verification.status === "approved" ? (
                              <FcApproval size={16} />
                            ) : (
                              <AiOutlineExclamationCircle
                                size={16}
                                className={`text-${
                                  doc.verification.status == "pending"
                                    ? "warning"
                                    : doc.verification.status == "approved"
                                    ? "success"
                                    : "danger"
                                }`}
                              />
                            )}
                            {doc.verification.status}
                          </div>
                        </div>

                        <div className="mb-3">
                          <div className="fw-semibold mb-1">Issues</div>
                          <div className="d-flex align-items-center gap-2 text-dark ">
                            {doc.issues[0]?.reason ?? "-"}
                          </div>
                        </div>

                        {doc.verification.status == "pending" && (
                          <>
                            <Form.Group className="mb-4">
                              <Form.Label className="fw-semibold">
                                Issue message
                              </Form.Label>
                              <Form.Control
                                as="textarea"
                                rows={3}
                                placeholder=""
                                value={issueTexts[doc.document_id] ?? ""}
                                onChange={(e) =>
                                  setIssueTexts({
                                    ...issueTexts,
                                    [doc.document_id]: e.target.value,
                                  })
                                }
                              />
                            </Form.Group>

                            <div className="d-flex gap-3">
                              <Button
                                variant="dark"
                                onClick={() => approveDocument(doc.document_id)}
                              >
                                Verify
                              </Button>
                              <Button
                                variant="outline-dark"
                                onClick={() =>
                                  raiseIssueOnDocument(doc.document_id)
                                }
                              >
                                Raise issue
                              </Button>
                            </div>
                          </>
                        )}
                      </Col>
                    )}
                  </Row>
                </Accordion.Body>
              </Accordion.Item>
            ))
          ) : (
            <div className="d-flex justify-content-center align-items-center">
              No Documents
            </div>
          )}
        </Accordion>
      </div>
      {loading.show && (
        <Loader show={loading.show} loadingMessage={loading.message} />
      )}
    </div>
  );
};

export default OnboardingDocumentReview;
